/**
 * pages/admin/healthCheck/checks.ts
 *
 * Initial check definitions for the AdminHealthCheck page.
 * Each entry maps to a runtime check executed in runners.ts.
 *
 * Updated: 2026-05-12 — added secdef_exception_justifications check (#15),
 *   secdef_authenticated_grants check (#16), and rls_tables_without_policies check (#17).
 */

import type { HealthCheck } from './types';

export const INITIAL_CHECKS: HealthCheck[] = [
  {
    id: 'auth',
    label: 'Profil utilisateur',
    description: 'Lecture du profil connecté depuis la table users',
    status: 'pending',
    detail: null,
  },
  {
    id: 'form_templates_own',
    label: 'Formulaires propres (utilisateur)',
    description: 'Un utilisateur peut lire ses propres form_templates',
    status: 'pending',
    detail: null,
  },
  {
    id: 'form_templates_admin',
    label: 'Formulaires admin visibles (critique)',
    description: 'Les formulaires créés par un admin (created_by NOT NULL) sont lisibles',
    status: 'pending',
    detail: null,
  },
  {
    id: 'assignments',
    label: 'Formulaires assignés',
    description: 'La table form_template_assignments est accessible',
    status: 'pending',
    detail: null,
  },
  {
    id: 'clients',
    label: 'Clients',
    description: 'La table clients est accessible et filtrée par utilisateur',
    status: 'pending',
    detail: null,
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'La table documents est accessible',
    status: 'pending',
    detail: null,
  },
  {
    id: 'shared_links',
    label: 'Liens partagés',
    description: 'La table shared_links est accessible',
    status: 'pending',
    detail: null,
  },
  {
    id: 'requests',
    label: 'Demandes (requests)',
    description: 'La table requests est accessible',
    status: 'pending',
    detail: null,
  },
  {
    id: 'user_delete_fk_integrity',
    label: 'Intégrité FK suppression utilisateur',
    description: 'Toutes les clés étrangères référençant les utilisateurs utilisent CASCADE ou SET NULL',
    status: 'pending',
    detail: null,
  },
  {
    id: 'vw_user_activity_summary',
    label: 'Vue analytics — résumé activité (security_invoker)',
    description: 'La vue vw_user_activity_summary est lisible par un admin (policies RLS des tables sous-jacentes intactes)',
    status: 'pending',
    detail: null,
  },
  {
    id: 'vw_admin_activity_events',
    label: 'Vue analytics — timeline événements (security_invoker)',
    description: 'La vue vw_admin_activity_events est lisible par un admin (policies RLS sur user_activity_events intactes)',
    status: 'pending',
    detail: null,
  },
  {
    id: 'secdef_public_grants',
    label: 'Fonctions SECURITY DEFINER exposées à anon/public',
    description: 'Aucune fonction SECURITY DEFINER du schéma public ne doit octroyer EXECUTE à anon ou PUBLIC',
    status: 'pending',
    detail: null,
  },
  {
    id: 'function_search_path',
    label: 'search_path verrouillé sur toutes les fonctions public',
    description: 'Toutes les fonctions PL/pgSQL du schéma public doivent avoir un search_path fixé pour éviter le search_path hijacking',
    status: 'pending',
    detail: null,
  },
  {
    id: 'storage_broad_select_policies',
    label: 'Policies SELECT larges sur storage.objects',
    description: 'Aucune policy SELECT publique (rôle PUBLIC) ne doit exister sur storage.objects — les buckets publics n\'en ont pas besoin',
    status: 'pending',
    detail: null,
  },
  {
    id: 'secdef_exception_justifications',
    label: 'Justifications SECURITY DEFINER documentées en base',
    description: 'Toutes les fonctions SECURITY DEFINER approuvées (exceptions anon/authenticated) ont un COMMENT commençant par "INTENTIONAL:" dans le catalogue Postgres',
    status: 'pending',
    detail: null,
  },
  {
    id: 'secdef_authenticated_grants',
    label: 'Fonctions SECURITY DEFINER non approuvées exposées à authenticated',
    description: 'Aucune fonction SECURITY DEFINER hors allowlist ne doit être accessible à authenticated — toute nouvelle fonction doit être ajoutée à l\'allowlist avec justification',
    status: 'pending',
    detail: null,
  },
  {
    id: 'rls_tables_without_policies',
    label: 'Tables RLS sans policy hors allowlist',
    description: 'Toute table avec RLS activé mais sans policy doit être dans l\'allowlist approuvée (service-role only) — digest_deliveries et notification_tokens sont approuvées',
    status: 'pending',
    detail: null,
  },
];
